/** Load an owner's saved condition before accepting edits or writing a replacement. */
import { useEffect, useRef, useState } from 'react';
import { selectCondition } from '../../../../utils/conditionSelection';

export function conditionSaveError(editor, enabled) {
  if (!enabled) return null;
  if (editor.status === 'loading') return 'Wait for the saved condition to load before saving.';
  if (editor.status === 'error') return 'The saved condition could not be loaded. Retry before saving.';
  const condition = editor.condition;
  if (!condition.targetSectionId || !condition.conditionOperator || !condition.value)
    return 'Please complete all required fields in the condition section.';
  return null;
}

export default function useConditionEditor({ tenantId, questionnaireId, ownerId, sectionId, isSectionCondition, isNew, fetchCondition }) {
  const key = JSON.stringify([tenantId, questionnaireId, isSectionCondition ? 'section' : 'question', ownerId]);
  const empty = () => ({ targetSectionId: 0, targetQuestionId: 0, questionaireId: questionnaireId,
    sectionId, ...(isSectionCondition ? {} : { questionId: ownerId }), value: '', isNew: true, tenantId });
  const [state, setState] = useState(() => ({ key, condition: empty(), status: isNew || !ownerId ? 'ready' : 'loading' }));
  const [retry, setRetry] = useState(0);
  const fetchRef = useRef(fetchCondition);
  fetchRef.current = fetchCondition;
  useEffect(() => {
    let cancelled = false;
    if (isNew || !ownerId) {
      setState({ key, condition: empty(), status: 'ready' });
      return () => { cancelled = true; };
    }
    setState({ key, condition: empty(), status: 'loading' });
    Promise.resolve().then(() => fetchRef.current(ownerId, tenantId)).then(rows => {
      if (cancelled) return;
      if (!Array.isArray(rows)) throw new Error('Condition fetch did not return records');
      const owned = rows.filter(row => isSectionCondition
        ? row.sectionId === ownerId && !row.questionId : row.questionId === ownerId);
      const selected = selectCondition(owned);
      setState({ key, condition: selected.condition ? { ...selected.condition } : empty(),
        warning: selected.warning, status: 'ready' });
    }).catch(() => { if (!cancelled) setState({ key, condition: empty(), status: 'error' }); });
    return () => { cancelled = true; };
    // Owner/retry changes intentionally fetch once. Editing or Redux refreshes must
    // never overwrite this editor's authored draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, isNew, retry]);
  const current = state.key === key ? state : { key, condition: empty(), status: 'loading' };
  return { ...current, edit: condition => setState(previous => previous.key === key && previous.status === 'ready'
    ? { ...previous, condition } : previous), retry: () => setRetry(value => value + 1) };
}
